import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Linking } from 'react-native';
import { router } from 'expo-router';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { TextLink } from '../common';
import { defensePlansApi } from '../../api/defensePlans';
import { useServiceAreasStore } from '../../store/serviceAreasStore';
import { suggestionsForPoint, suggestionsForStructure } from '../../utils/planGeometry';
import { DEFENSE_STRATEGIES, DEFENSE_STRATEGY_ORDER } from '../map/defenseStrategies';
import type { DefensePlan, DefenseStrategy } from '../../types';
import { createPrefixedLogger } from '../../utils/logger';

const log = createPrefixedLogger('PlanSuggestions');

// A single plan's title is worth naming outright when it's short enough to
// read inline ("Glenn's plan"); a long custom title would crowd the line, so
// past this it's left out — the strategy names still link to the plan.
const PLAN_TITLE_INLINE_MAX = 24;

interface PlanSuggestionsProps {
  structureId: string;
  latitude: number;
  longitude: number;
}

// "Planned for this home: General hardening, Surface treatment" — every distinct
// strategy a shared plan has drawn directly onto this structure (a home
// feature, see suggestionsForStructure), each linking to its plan. Shown
// ahead of the ground-level, area-based suggestions below it: a defense
// picked out for this exact house is a stronger match than one inferred from
// a drawn area it happens to sit inside.
function HomeSuggestionsRow({
  suggestions,
}: {
  suggestions: { strategy: DefenseStrategy; plan: DefensePlan }[];
}) {
  const { colors } = useTheme();
  const singlePlan = suggestions.every((s) => s.plan.id === suggestions[0].plan.id)
    ? suggestions[0].plan
    : null;
  const planTitleSuffix =
    singlePlan && singlePlan.title.length <= PLAN_TITLE_INLINE_MAX ? ` (${singlePlan.title})` : '';
  return (
    <View style={styles.row} testID="structure-plan-home-suggestions">
      <Text style={[styles.text, { color: colors.textSecondary }]}>Planned for this home:</Text>
      {suggestions.map(({ strategy, plan }, i) => (
        <React.Fragment key={strategy}>
          <TextLink
            label={DEFENSE_STRATEGIES[strategy].label}
            onPress={() => router.push({ pathname: '/plan/[id]', params: { id: plan.id } })}
            accessibilityLabel={`${DEFENSE_STRATEGIES[strategy].label}, view this plan`}
            textStyle={styles.text}
          />
          <Text style={[styles.text, { color: colors.textSecondary }]}>
            {i < suggestions.length - 1 ? ',' : planTitleSuffix}
          </Text>
        </React.Fragment>
      ))}
    </View>
  );
}

/**
 * "Neighborhood plans suggest: <strategy>" — one line per strategy whose
 * area a shared defense plan has drawn over this structure's point, each
 * linking to that plan. A one-shot read, not a subscription: the property
 * page doesn't need to stay live for a plan someone else might be drawing
 * right now, the same way ChecklistPreview's evidence fetch is one-shot.
 *
 * Scoped the same way the map's own "Plans in this area" list is
 * (subscribeAreaPlans: areaId + visibility='shared') — see
 * useMapSubscriptions.ts and defensePlansStore.subscribeAreaPlans — just
 * resolved for this fixed point instead of the viewport's center, and read
 * once instead of streamed.
 */
export function PlanSuggestions({ structureId, latitude, longitude }: PlanSuggestionsProps) {
  const { colors } = useTheme();
  // Raw plans, not the derived suggestion maps: both suggestionsForStructure
  // and suggestionsForPoint read the same one-shot area fetch, so there's no
  // reason to fetch (or shape) it twice.
  const [plans, setPlans] = useState<DefensePlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    const areaId =
      useServiceAreasStore.getState().findContaining(latitude, longitude)?.id ?? 'unassigned';
    defensePlansApi
      .getAreaPlans(areaId)
      .then((result) => {
        if (!cancelled) setPlans(result);
      })
      .catch((err) => {
        log.warn('getAreaPlans failed:', err);
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude]);

  const homeSuggestions = suggestionsForStructure(plans, structureId).sort(
    (a, b) =>
      DEFENSE_STRATEGY_ORDER.indexOf(a.strategy) - DEFENSE_STRATEGY_ORDER.indexOf(b.strategy)
  );
  const suggestions = suggestionsForPoint(plans, latitude, longitude);
  const strategies = DEFENSE_STRATEGY_ORDER.filter((s) => (suggestions[s]?.length ?? 0) > 0);
  if (homeSuggestions.length === 0 && strategies.length === 0) return null;

  return (
    <View style={styles.wrapper} testID="structure-plan-suggestions">
      {homeSuggestions.length > 0 && <HomeSuggestionsRow suggestions={homeSuggestions} />}
      {strategies.map((strategy) => {
        const def = DEFENSE_STRATEGIES[strategy];
        const plan = suggestions[strategy]![0];
        return (
          <View key={strategy} style={styles.row} testID={`structure-plan-suggestion-${strategy}`}>
            <Text style={[styles.text, { color: colors.textSecondary }]}>
              Neighborhood plans suggest:
            </Text>
            <TextLink
              label={def.label}
              onPress={() => router.push({ pathname: '/plan/[id]', params: { id: plan.id } })}
              accessibilityLabel={`${def.label}, view neighborhood plan`}
              textStyle={styles.text}
            />
            {def.learnMoreUrl && (
              <TextLink
                label="Learn more"
                onPress={() => void Linking.openURL(def.learnMoreUrl!)}
                color={colors.textTertiary}
                textStyle={styles.text}
              />
            )}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: SPACING.xxs,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACING.xxs,
  },
  text: {
    ...TYPOGRAPHY.bodySmall,
  },
});
